import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { hasIdentityCallback, processIdentityCallback } from "../Worksuite/auth/identityCallback";
import { clearIdentitySession, readStoredUser } from "../Worksuite/auth/authStorage";
import { verifyCampaignsIdentitySession } from "../Worksuite/auth/identitySession";
import { installCampaignsSessionLifecycle } from "../Worksuite/auth/identityLifecycle";

const authorizeContext = createContext(null);

const AuthorizationProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(() => readStoredUser() || {});
  const [status, setStatus] = useState("initializing");
  const [reason, setReason] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (hasIdentityCallback()) {
          const result = await processIdentityCallback();
          if (!active || !result) return;
          setUser(result.user || {});
          setStatus("authenticated");
          navigate(result.destination, { replace: true });
          return;
        }
        const storedUser = readStoredUser();
        if (!storedUser) {
          if (active) setStatus("unauthenticated");
          return;
        }
        const verification = await verifyCampaignsIdentitySession();
        if (!active) return;
        if (verification.status === "active") {
          setUser(storedUser);
          setStatus("authenticated");
        } else if (verification.status === "unverifiable") {
          setUser(storedUser);
          setReason(verification.reason);
          setStatus("unverifiable");
        } else {
          setUser({});
          setReason(verification.reason);
          setStatus("unauthenticated");
        }
      } catch (error) {
        if (!active) return;
        console.error("Campaigns identity bootstrap failed", error);
        clearIdentitySession();
        setUser({});
        setReason(error?.message || "identity_bootstrap_failed");
        setStatus("unauthenticated");
      }
    })();
    return () => { active = false; };
  }, [navigate]);

  useEffect(() => installCampaignsSessionLifecycle({
    onActive: () => {
      const storedUser = readStoredUser();
      if (storedUser) { setUser(storedUser); setReason(""); setStatus("authenticated"); }
    },
    onInactive: (result) => {
      clearIdentitySession();
      setUser({});
      setReason(result?.reason || "session_inactive");
      setStatus("unauthenticated");
      const protectedPath = !["/login", "/signup"].includes(window.location.pathname);
      if (protectedPath) navigate("/login", { replace: true, state: { from: location } });
    },
    onUnverifiable: (result) => {
      setReason(result?.reason || "accounts_unavailable");
      setStatus((current) => current === "authenticated" ? current : "unverifiable");
    },
  }), [location, navigate]);

  const authorize = (loggedIn, callback) => {
    if (loggedIn) setStatus("authenticated");
    else { clearIdentitySession(); setUser({}); setStatus("unauthenticated"); }
    if (typeof callback === "function") callback(setUser);
  };

  const logout = () => {
    clearIdentitySession();
    setUser({});
    setStatus("unauthenticated");
    navigate("/login", { replace: true, state: { from: location } });
  };

  const value = useMemo(() => ({ authorize, logout, reason, setUser, status, user }), [reason, status, user]);
  return <authorizeContext.Provider value={value}>{children}</authorizeContext.Provider>;
};

function useAuthContext() {
  const context = useContext(authorizeContext);
  if (!context) throw new Error("AuthorizationProvider is required");
  return context;
}

const useAuthorize = () => useAuthContext().authorize;
const useUser = () => useAuthContext().user;
const useSetUser = () => useAuthContext().setUser;
const useSetContent = () => () => {};
const useAuthStatus = () => useAuthContext();

export default AuthorizationProvider;
export { useAuthorize, useUser, useSetUser, useSetContent, useAuthStatus };
